import React, { useState, useEffect } from 'react';
import { Table, Input, Select, DatePicker, Button, Space, Modal, Descriptions, message, Form, InputNumber, Timeline, Alert, Tooltip } from 'antd';
import { ReloadOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_TEXT = {
  pending: '待支付',
  processing: '处理中',
  paid: '已支付',
  failed: '失败',
  expired: '已过期',
  closed: '已关闭'
};

const STATUS_TONE = {
  pending: 'pending',
  processing: 'processing',
  paid: 'paid',
  failed: 'failed',
  expired: 'muted',
  closed: 'muted'
};

const CURRENCY_SYMBOLS = { CNY: '¥', USD: '$', EUR: '€', GBP: '£', KES: 'KSh' };

const PAYMENT_METHODS = {
  bank_transfer: '银行转账',
  alipay: '支付宝',
  wechat: '微信',
  usdt: 'USDT',
  'm-pesa': 'M-Pesa',
  mobile_money: 'Mobile Money',
  united_bank: 'United bank'
};

const formatMoney = (val, currencyCode = 'CNY') => {
  const code = currencyCode || 'CNY';
  const symbol = CURRENCY_SYMBOLS[code] || '';
  const num = Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return { symbol, num, code };
};

const renderAmount = (val, currencyCode) => {
  const { symbol, num, code } = formatMoney(val, currencyCode);
  return (
    <div className="tp-amount">
      <span className="tp-amount__value">{symbol}{num}</span>
      <span className="tp-amount__ccy">{code}</span>
    </div>
  );
};

const renderKv = (info, max = 2) => {
  if (!info) return <span className="tp-empty">-</span>;
  let data = info;
  if (typeof info === 'string') {
    try { data = JSON.parse(info); } catch (e) { return <span className="tp-ellipsis" title={info}>{info}</span>; }
  }
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <span className="tp-empty">-</span>;
  return (
    <div className="tp-kv">
      {entries.slice(0, max).map(([k, v]) => (
        <div key={k} className="tp-kv__row">
          <span className="tp-kv__k">{k}</span>
          <span className="tp-kv__v" title={String(v)}>{String(v)}</span>
        </div>
      ))}
      {entries.length > max && <div className="tp-kv__more">+{entries.length - max}</div>}
    </div>
  );
};

const ThirdPartyOrders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currencyStats, setCurrencyStats] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({});
  const [apps, setApps] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmForm] = Form.useForm();
  const [changeVisible, setChangeVisible] = useState(false);
  const [changeOrder, setChangeOrder] = useState(null);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeForm] = Form.useForm();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const changeTargetStatus = Form.useWatch('target_status', changeForm);

  useEffect(() => {
    fetchApps();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [page, filters]);

  const fetchApps = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { status: 1, limit: 100 } });
      setApps(response.data.list || []);
    } catch (error) {
      console.error('获取应用列表失败', error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...filters
      };
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
        delete params.dateRange;
      }
      const response = await api.get('/admin/third-party/orders', { params });
      setOrders(response.data.list || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalAmount(response.data.total_amount || 0);

      const statParams = { ...params };
      delete statParams.page;
      delete statParams.limit;
      const statResponse = await api.get('/admin/third-party/orders/statistics', { params: statParams });
      setCurrencyStats(statResponse.data.currency_stats || []);
    } catch (error) {
      message.error('获取订单列表失败');
    }
    setLoading(false);
  };

  const handleSearch = (values) => {
    setFilters(values);
    setPage(1);
  };

  const handleReset = () => {
    setFilters({});
    setPage(1);
  };

  const handleViewDetail = async (record) => {
    try {
      setHistoryList([]);
      setHistoryLoading(true);
      const [detailResponse, historyResponse] = await Promise.all([
        api.get(`/admin/third-party/orders/${record.trade_no}`),
        api.get(`/admin/third-party/orders/${record.trade_no}/history`)
      ]);
      setOrderDetail(detailResponse.data);
      setHistoryList(historyResponse.data || []);
      setDetailVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleNotify = async (tradeNo) => {
    try {
      await api.post(`/admin/third-party/orders/${tradeNo}/notify`);
      message.success('通知已发送');
    } catch (error) {
      message.error('通知发送失败');
    }
  };

  const handleConfirmPay = async (record) => {
    try {
      const response = await api.get(`/admin/third-party/orders/${record.trade_no}`);
      setConfirmOrder(response.data);
      setConfirmVisible(true);
      confirmForm.setFieldsValue({ actual_amount: parseFloat(response.data.total_amount) });
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  const handleConfirmPaySubmit = async () => {
    try {
      const values = await confirmForm.validateFields();
      setConfirmLoading(true);
      await api.post(`/admin/third-party/orders/${confirmOrder.trade_no}/confirm`, {
        actual_amount: values.actual_amount
      });
      message.success('已确认支付');
      setConfirmVisible(false);
      setConfirmOrder(null);
      confirmForm.resetFields();
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleRejectPay = async (tradeNo) => {
    try {
      const reason = prompt('请输入拒绝原因:');
      if (reason === null) return;
      await api.post(`/admin/third-party/orders/${tradeNo}/reject`, { reason });
      message.success('已拒绝');
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleChange = async (record) => {
    try {
      const response = await api.get(`/admin/third-party/orders/${record.trade_no}`);
      setChangeOrder(response.data);
      setChangeVisible(true);
      changeForm.setFieldsValue({
        target_status: response.data.status === 'paid' ? 'failed' : 'paid',
        actual_amount: parseFloat(response.data.actual_amount || response.data.total_amount)
      });
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  const handleChangeSubmit = async () => {
    try {
      const values = await changeForm.validateFields();
      setChangeLoading(true);
      await api.post(`/admin/third-party/orders/${changeOrder.trade_no}/status`, {
        target_status: values.target_status,
        reason: values.reason,
        actual_amount: values.target_status === 'paid' ? values.actual_amount : undefined
      });
      message.success('订单状态已变更');
      setChangeVisible(false);
      setChangeOrder(null);
      changeForm.resetFields();
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    } finally {
      setChangeLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const tone = STATUS_TONE[status] || 'muted';
    return <span className={`tp-tag tp-tag--${tone}`}>{STATUS_TEXT[status] || status}</span>;
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 196,
      render: (val) => val ? <span className="tp-ellipsis" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 110,
      render: (val) => val ? <span className="tp-ellipsis" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '商品标题',
      dataIndex: 'subject',
      key: 'subject',
      width: 160,
      render: (val) => val ? <span className="tp-ellipsis" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '验证短码',
      dataIndex: 'short_code',
      key: 'short_code',
      width: 110,
      render: (code) => code || <span className="tp-empty">-</span>
    },
    {
      title: '金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 140,
      render: (val, record) => renderAmount(val, record.currency_code)
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 110,
      render: (method) => {
        const label = PAYMENT_METHODS[method] || method;
        return label || <span className="tp-empty">-</span>;
      }
    },
    {
      title: '付款人',
      dataIndex: 'payer_info',
      key: 'payer_info',
      width: 180,
      render: (info) => renderKv(info, 2)
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => (
        <div className="tp-status">
          {getStatusTag(status)}
          {record.is_modified == 1 && (
            <span className="tp-tag tp-tag--modified">已修改 ×{record.modify_count || 1}</span>
          )}
        </div>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => time ? <span className="tp-time">{dayjs(time).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <div className="tp-actions">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {(record.status === 'processing' || record.status === 'pending') && (
            <>
              <Button type="link" size="small" className="tp-link--ok" onClick={() => handleConfirmPay(record)}>
                确认支付
              </Button>
              <Button type="link" size="small" className="tp-link--danger" onClick={() => handleRejectPay(record.trade_no)}>
                拒绝
              </Button>
            </>
          )}
          {record.status === 'paid' && (
            <Button type="link" size="small" onClick={() => handleNotify(record.trade_no)}>
              通知
            </Button>
          )}
          {(record.status === 'paid' || record.status === 'failed') && (
            <Tooltip title={`变更订单状态（当前：${STATUS_TEXT[record.status] || record.status}）`}>
              <Button type="link" size="small" className="tp-link--change" icon={<EditOutlined />} onClick={() => handleChange(record)}>
                变更
              </Button>
            </Tooltip>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="tp-page">
      <div className="tp-card tp-toolbar">
        <div className="tp-filters">
          <Select
            placeholder="选择应用"
            allowClear
            className="tp-field--md"
            onChange={(val) => setFilters({ ...filters, app_id: val })}
            value={filters.app_id}
          >
            {apps.map(app => (
              <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
            ))}
          </Select>
          <Select
            placeholder="订单状态"
            allowClear
            className="tp-field--sm"
            onChange={(val) => setFilters({ ...filters, status: val })}
            value={filters.status}
          >
            <Option value="pending">待支付</Option>
            <Option value="processing">处理中</Option>
            <Option value="paid">已支付</Option>
            <Option value="failed">失败</Option>
            <Option value="expired">已过期</Option>
            <Option value="closed">已关闭</Option>
          </Select>
          <Select
            placeholder="支付方式"
            allowClear
            className="tp-field--sm"
            onChange={(val) => setFilters({ ...filters, payment_method: val })}
            value={filters.payment_method}
          >
            {/* <Option value="bank_transfer">银行转账</Option> */}
            {/* <Option value="alipay">支付宝</Option> */}
            {/* <Option value="wechat">微信</Option> */}
            <Option value="usdt">USDT</Option>
            <Option value="m-pesa">M-Pesa</Option>
            <Option value="mobile_money">Mobile Money</Option>
            <Option value="united_bank">United bank</Option>
          </Select>
          <RangePicker
            className="tp-field--date"
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            value={filters.dateRange}
          />
          <Input
            placeholder="订单号 / 商户订单号"
            className="tp-field--lg"
            allowClear
            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            value={filters.keyword}
          />
          <div className="tp-filters__actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchOrders()}>
              搜索
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </div>
        </div>
      </div>

      <div className="tp-card tp-summary">
        <div className="tp-summary__item">
          <div className="tp-summary__label">订单总数</div>
          <div className="tp-summary__value">{Number(total || 0).toLocaleString('en-US')}</div>
        </div>
        {currencyStats.map((item, index) => {
          const { symbol, num, code } = formatMoney(item.paid_amount, item.currency_code);
          return (
            <div className="tp-summary__item" key={item.currency_code || index}>
              <div className="tp-summary__label">已付金额（{code}）</div>
              <div className="tp-summary__value tp-summary__value--accent">{symbol}{num}</div>
            </div>
          );
        })}
      </div>

      <div className="tp-card tp-table-card tp-table-12">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          size="middle"
          scroll={{ x: 1520 }}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`
          }}
        />
      </div>

      <Modal
        className="tp-modal"
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {orderDetail && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="平台订单号" span={2}>{orderDetail.trade_no}</Descriptions.Item>
            <Descriptions.Item label="商户订单号" span={2}>{orderDetail.out_trade_no}</Descriptions.Item>
            <Descriptions.Item label="应用名称">{orderDetail.app_name}</Descriptions.Item>
            <Descriptions.Item label="状态">{getStatusTag(orderDetail.status)}</Descriptions.Item>
            <Descriptions.Item label="商品标题" span={2}>{orderDetail.subject}</Descriptions.Item>
            <Descriptions.Item label="货币代码">{orderDetail.currency_code || 'CNY'}</Descriptions.Item>
            <Descriptions.Item label="订单金额">
              {(() => {
                const { symbol, num, code } = formatMoney(orderDetail.total_amount, orderDetail.currency_code);
                return `${symbol}${num} ${code}`;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="实际金额">
              {orderDetail.actual_amount ? (() => {
                const { symbol, num, code } = formatMoney(orderDetail.actual_amount, orderDetail.currency_code);
                return `${symbol}${num} ${code}`;
              })() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="支付方式">
              {PAYMENT_METHODS[orderDetail.payment_method] || orderDetail.payment_method || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="第三方交易号">{orderDetail.transaction_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="转账人">
              {orderDetail.payer_name || '-'}
              {orderDetail.payer_name_submitted_at && (
                <div className="tp-muted">
                  提交于 {dayjs(orderDetail.payer_name_submitted_at).format('YYYY-MM-DD HH:mm:ss')}
                </div>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="支付凭证" span={2}>
              {orderDetail.payment_info ? (
                typeof orderDetail.payment_info === 'string' 
                  ? orderDetail.payment_info 
                  : JSON.stringify(orderDetail.payment_info)
              ) : '-'}
            </Descriptions.Item>
            {orderDetail.account_info && (
              <>
                <Descriptions.Item label="收款账户" span={2}>
                  {orderDetail.payment_method === 'bank_transfer' && orderDetail.account_info.bank_name && 
                    `${orderDetail.account_info.bank_name} - ${orderDetail.account_info.account_name} (${orderDetail.account_info.bank_account})`}
                  {orderDetail.payment_method === 'alipay' && orderDetail.account_info.alipay_account && 
                    `${orderDetail.account_info.alipay_account} - ${orderDetail.account_info.account_name}`}
                  {orderDetail.payment_method === 'wechat' && orderDetail.account_info.wechat_account && 
                    `${orderDetail.account_info.wechat_account}`}
                  {orderDetail.payment_method === 'usdt' && orderDetail.account_info.usdt_address && 
                    `${orderDetail.account_info.usdt_address} (${orderDetail.account_info.usdt_network})`}
                  {orderDetail.payment_method === 'm-pesa' && orderDetail.account_info.mpesa_phone && 
                    `${orderDetail.account_info.mpesa_phone} - ${orderDetail.account_info.mpesa_name}`}
                  {orderDetail.payment_method === 'mobile_money' && orderDetail.account_info.mpesa_phone && 
                    `${orderDetail.account_info.mpesa_phone} - ${orderDetail.account_info.mpesa_name}`}
                </Descriptions.Item>
              </>
            )}
            {orderDetail.payer_info && (
              <Descriptions.Item label="付款人信息" span={2}>
                {typeof orderDetail.payer_info === 'object' 
                  ? Object.entries(orderDetail.payer_info).map(([key, val]) => (
                      <div key={key}><span className="tp-muted">{key}:</span> {val}</div>
                    ))
                  : orderDetail.payer_info}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">{orderDetail.created_at ? dayjs(orderDetail.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="支付时间">{orderDetail.payment_time ? dayjs(orderDetail.payment_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="过期时间">{orderDetail.expire_time ? dayjs(orderDetail.expire_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="回调地址" span={2}>
              {orderDetail.callback_url ? (
                <a href={orderDetail.callback_url} target="_blank" rel="noopener noreferrer">{orderDetail.callback_url}</a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="异步通知地址" span={2}>
              {orderDetail.notify_url ? (
                <a href={orderDetail.notify_url} target="_blank" rel="noopener noreferrer">{orderDetail.notify_url}</a>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        <div className="tp-history">
          <h3 className="tp-history__title">审核 / 变更记录</h3>
          {historyLoading ? (
            <div className="tp-history__empty">加载中...</div>
          ) : historyList.length === 0 ? (
            <div className="tp-history__empty">暂无记录</div>
          ) : (
            <Timeline
              items={historyList.map((item) => ({
                color: item.is_reversal ? 'red' : (item.new_status === 'paid' ? 'green' : 'orange'),
                children: (
                  <div>
                    <Space size={4} wrap>
                      <span className="tp-history__meta">{dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}</span>
                      <span className={`tp-tag ${item.is_modified ? 'tp-tag--modified' : 'tp-tag--pending'}`}>{item.is_modified ? '变更' : '确认'}</span>
                      {item.is_reversal ? <span className="tp-tag tp-tag--failed">冲正</span> : null}
                    </Space>
                    <div className="tp-history__flow">
                      {STATUS_TEXT[item.old_status] || item.old_status || '-'}
                      {' → '}
                      {STATUS_TEXT[item.new_status] || item.new_status}
                    </div>
                    {item.amount != null && (
                      <div className="tp-history__line">
                        {item.original_amount != null ? (
                          <>金额: <span className="tp-history__muted tp-strike">{item.original_amount}</span> → <strong>{item.amount}</strong> CNY</>
                        ) : (
                          <>金额: {item.amount} CNY</>
                        )}
                      </div>
                    )}
                    {item.settle_amount_cny != null && (
                      <div className="tp-history__line">
                        结算金额: {item.settle_amount_cny} CNY
                        {item.is_reversal ? <span className="tp-tag tp-tag--failed tp-gap-left">冲正扣除</span> : null}
                      </div>
                    )}
                    {item.operator_name && (
                      <div className="tp-history__line tp-history__muted">操作人: {item.operator_name}</div>
                    )}
                    {item.remark && (
                      <div className="tp-history__line">备注: {item.remark}</div>
                    )}
                  </div>
                ),
              }))}
            />
          )}
        </div>
      </Modal>

      <Modal
        className="tp-modal"
        title="确认支付"
        open={confirmVisible}
        onOk={handleConfirmPaySubmit}
        onCancel={() => {
          setConfirmVisible(false);
          setConfirmOrder(null);
          confirmForm.resetFields();
        }}
        confirmLoading={confirmLoading}
        okText="确认支付"
        cancelText="取消"
        width={600}
      >
        {confirmOrder && (
          <>
            <Descriptions column={2} bordered size="small" className="tp-confirm-desc">
              <Descriptions.Item label="订单号" span={2}>{confirmOrder.trade_no}</Descriptions.Item>
              <Descriptions.Item label="应用名称">{confirmOrder.app_name}</Descriptions.Item>
              <Descriptions.Item label="支付方式">
                {PAYMENT_METHODS[confirmOrder.payment_method] || confirmOrder.payment_method}
              </Descriptions.Item>
              <Descriptions.Item label="商品标题" span={2}>{confirmOrder.subject}</Descriptions.Item>
              {confirmOrder.payer_info && (
                <Descriptions.Item label="付款人信息" span={2}>
                  {typeof confirmOrder.payer_info === 'object'
                    ? Object.entries(confirmOrder.payer_info).map(([key, val]) => (
                        <div key={key}><span className="tp-muted">{key}:</span> {val}</div>
                      ))
                    : confirmOrder.payer_info}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="原始金额" span={2}>
                {(() => {
                  const { symbol, num, code } = formatMoney(confirmOrder.total_amount, confirmOrder.currency_code);
                  return <span className="tp-amount__value">{symbol}{num} {code}</span>;
                })()}
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={confirmForm}
              layout="vertical"
              onFinish={handleConfirmPaySubmit}
            >
              <Form.Item
                label="实际支付金额"
                name="actual_amount"
                rules={[
                  { required: true, message: '请输入实际支付金额' },
                  { type: 'number', min: 0.01, message: '金额必须大于0' }
                ]}
                extra="请根据实际到账金额填写，将用于结算"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  step={0.01}
                  precision={2}
                  placeholder="请输入实际支付金额"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        className="tp-modal"
        title="变更订单状态"
        open={changeVisible}
        onCancel={() => {
          setChangeVisible(false);
          setChangeOrder(null);
          changeForm.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {changeOrder && (
          <Form
            form={changeForm}
            layout="vertical"
            onFinish={handleChangeSubmit}
          >
            {changeTargetStatus === 'failed' && (
              <Form.Item>
                <Alert
                  type="warning"
                  showIcon
                  message="该操作将冲回商户余额"
                  description="若该订单已结算入账，将按结算金额从商户余额冲回对应款项，并新增一条结算冲正流水。若商户余额不足将无法变更。"
                />
              </Form.Item>
            )}
            {changeTargetStatus === 'paid' && (
              <Form.Item>
                <Alert
                  type="info"
                  showIcon
                  message="重新确认为已支付"
                  description="确认后该订单将重新创建结算记录，并按自动结算配置将结算金额入账到商户余额。"
                />
              </Form.Item>
            )}
            <Form.Item label="订单号">
              <Input value={changeOrder.trade_no} disabled />
            </Form.Item>
            <Form.Item label="应用名称">
              <Input value={changeOrder.app_name} disabled />
            </Form.Item>
            <Form.Item label="当前状态">
              <Input value={STATUS_TEXT[changeOrder.status] || changeOrder.status} disabled />
            </Form.Item>
            <Form.Item label="订单金额">
              <Input value={`${changeOrder.total_amount} ${changeOrder.currency_code || 'CNY'}`} disabled />
            </Form.Item>
            <Form.Item
              label="目标状态"
              name="target_status"
              rules={[{ required: true, message: '请选择目标状态' }]}
            >
              <Select placeholder="请选择目标状态">
                <Option value="paid">已支付</Option>
                <Option value="failed">失败</Option>
              </Select>
            </Form.Item>
            {changeTargetStatus === 'paid' && (
              <Form.Item
                label="实际支付金额"
                name="actual_amount"
                rules={[
                  { required: true, message: '请输入实际支付金额' },
                  { type: 'number', min: 0.01, message: '金额必须大于0' }
                ]}
                extra="请输入实际支付金额，将用于重新计算结算金额"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  step={0.01}
                  precision={2}
                  placeholder="请输入实际支付金额"
                  addonAfter={changeOrder ? (changeOrder.currency_code || 'CNY') : 'CNY'}
                />
              </Form.Item>
            )}
            <Form.Item
              label="变更备注"
              name="reason"
              rules={[{ required: true, message: '请输入变更备注' }]}
            >
              <Input.TextArea rows={3} placeholder="请输入变更备注" />
            </Form.Item>
            <Form.Item className="tp-modal-actions">
              <Space>
                <Button onClick={() => setChangeVisible(false)}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit" loading={changeLoading}>
                  确认变更
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default ThirdPartyOrders;
